import config from "../config";
import { Region } from "../graphql";
import { auth } from "../keycloak";
import { fetchFinToken } from "../lib/support/finToken";
import { router } from "../router";
import { reportSilently } from "./reportError";
import { getCurrentRegion } from "./route";

interface IntercomClaims {
  sub?: string;
  email?: string;
  preferred_username?: string;
  given_name?: string;
  family_name?: string;
}

let booted = false;

/**
 * Boots the Intercom messenger with Fin (AI support bot) authentication.
 *
 * Mirrors the website's `useIntercom` (app/composables/useIntercom.ts): fetch a
 * signed JWT and pass it to Intercom as `intercom_user_jwt`. The only
 * difference is transport — NGP fetches the token via the REST proxy instead of
 * gRPC-web (see lib/support/finToken.ts).
 *
 * Caller is responsible for gating on BOTH the `support-chat` feature flag and
 * `config.intercom.enabled` — see app.vue for why each is needed.
 *
 * Boots at most once per page load. Never throws or surfaces anything to the
 * user: any failure (dynamic import, token fetch, SDK init) is swallowed and
 * reported to Sentry.
 */
export async function bootIntercom(): Promise<void> {
  if (booted || !auth.isAuthenticated) {
    return;
  }
  const claims = (auth.accessTokenParsed ?? {}) as IntercomClaims;
  if (!claims.sub) {
    return;
  }

  const region = getCurrentRegion(router.currentRoute.value) ?? Region.Eu;

  try {
    const [{ default: Intercom }, token] = await Promise.all([
      import("@intercom/messenger-js-sdk"),
      fetchFinToken(region),
    ]);

    if (!token) {
      return;
    }

    // Guard again: the awaits above yield, and a parallel call could have
    // booted. Claim the slot before the (synchronous) Intercom() call so a
    // concurrent invocation can't double-boot.
    if (booted) {
      return;
    }
    booted = true;

    const name = `${claims.given_name ?? ""} ${claims.family_name ?? ""}`.trim() || claims.preferred_username;

    Intercom({
      app_id: config.intercom.appId,
      region: config.intercom.region,
      user_id: claims.sub,
      name,
      email: claims.email,
      intercom_user_jwt: token,
    });
  } catch (err) {
    reportSilently(err, { feature: "intercom", op: "bootIntercom" });
  }
}
